// @deprecated - Use feature hooks from @/features/*/hooks.ts

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabaseClient";

// Types (UUID compatible)
export interface Struttura {
  id: string;
  nome: string;
  indirizzo?: string;
  citta?: string;
  cap?: string;
  telefono?: string;
  email?: string;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Prenotazione {
  id: string;
  struttura_id: string;
  struttura_nome?: string;
  camera_id?: string;
  camera_nome?: string;
  ospite_nome: string;
  ospite_email?: string;
  ospite_telefono?: string;
  check_in: string;
  check_out?: string;
  data_checkin?: string;
  data_checkout?: string;
  numero_ospiti?: number;
  importo?: string;
  stato: string;
  piattaforma?: string;
  numero?: string;
  da_pagare?: number;
  note?: string;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
}

// Strutture queries
export function useGetStrutture() {
  return useQuery({
    queryKey: ["strutture"],
    queryFn: async (): Promise<Struttura[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("strutture")
        .select("*")
        .eq("user_id", user.id)
        .order("nome");
      
      if (error) throw error;
      return data || [];
    },
  });
}

export function useGetStruttura(id: string, options?: { query?: { enabled?: boolean; queryKey?: any[] } }) {
  return useQuery({
    queryKey: options?.query?.queryKey || ["struttura", id],
    queryFn: async (): Promise<Struttura | null> => {
      if (!id) return null;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("strutture")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: options?.query?.enabled !== false && !!id,
  });
}

export function useCreateStruttura() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (struttura: Omit<Struttura, "id" | "created_at" | "updated_at">): Promise<Struttura> => {
      const { data: { user } } = await supabase.auth.getUser();
      let authenticatedUser = user;
      
      if (!user) {
        // Try to get session as fallback
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          throw new Error("Not authenticated - Please login first");
        }
        authenticatedUser = session.user;
      }
      
      console.log("Creating struttura with user_id:", authenticatedUser.id);
      
      const { data, error } = await supabase
        .from("strutture")
        .insert({ ...struttura, user_id: authenticatedUser.id })
        .select()
        .single();
      
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      if (!data) throw new Error("Failed to create struttura");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["strutture"] });
    },
  });
}

export function useUpdateStruttura() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Struttura> }): Promise<Struttura> => {
      const { data: result, error } = await supabase
        .from("strutture")
        .update(data)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      if (!result) throw new Error("Failed to update struttura");
      return result;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["strutture"] });
      qc.invalidateQueries({ queryKey: ["struttura", variables.id] });
    },
  });
}

// Prenotazioni queries
export function useGetPrenotazioni() {
  return useQuery({
    queryKey: ["prenotazioni"],
    queryFn: async (): Promise<Prenotazione[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("prenotazioni")
        .select("*")
        .eq("user_id", user.id)
        .order("check_in", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreatePrenotazione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (prenotazione: Omit<Prenotazione, "id" | "created_at" | "updated_at">): Promise<Prenotazione> => {
      const { data: { user } } = await supabase.auth.getUser();
      let authenticatedUser = user;
      
      if (!user) {
        // Try to get session as fallback
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          throw new Error("Not authenticated - Please login first");
        }
        authenticatedUser = session.user;
      }
      
      console.log("Creating prenotazione with user_id:", authenticatedUser.id);
      
      const { data, error } = await supabase
        .from("prenotazioni")
        .insert({ ...prenotazione, user_id: authenticatedUser.id })
        .select()
        .single();
      
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      if (!data) throw new Error("Failed to create prenotazione");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prenotazioni"] });
    },
  });
}

export function useUpdatePrenotazione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Prenotazione> }): Promise<Prenotazione> => {
      const { data: result, error } = await supabase
        .from("prenotazioni")
        .update(data)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      if (!result) throw new Error("Failed to update prenotazione");
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prenotazioni"] });
    },
  });
}

export function useDeletePrenotazione() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const { error } = await supabase
        .from("prenotazioni")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prenotazioni"] });
    },
  });
}

// Camera types and queries (stub for now)
export interface Camera {
  id: number;
  nome: string;
  struttura_id: number;
  struttura_nome?: string;
  tipo?: string;
  posti_letto?: number;
  prezzo_base?: number;
  descrizione?: string;
  codice_camera?: string;
  piano?: string;
  locazione?: string;
  locazione_indirizzo?: string;
  check_in?: string;
  check_out?: string;
  codice_accesso?: string;
  foto?: string;
  dotazioni?: string;
  stato?: string;
  colore?: string;
  disponibile?: boolean;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
}

export function useGetCamera(id: number, options?: { query?: { enabled?: boolean; queryKey?: any[] } }) {
  return useQuery({
    queryKey: options?.query?.queryKey || ["camera", id],
    queryFn: async (): Promise<Camera | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("camere")
        .select("*")
        .eq("id", id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: options?.query?.enabled !== false && !!id,
  });
}

export function useCreateCamera() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (camera: Omit<Camera, "id" | "created_at" | "updated_at">): Promise<Camera> => {
      const { data: { user } } = await supabase.auth.getUser();
      let authenticatedUser = user;
      
      if (!user) {
        // Try to get session as fallback
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          throw new Error("Not authenticated - Please login first");
        }
        authenticatedUser = session.user;
      }
      
      console.log("Creating camera with user_id:", authenticatedUser.id);
      
      const { data, error } = await supabase
        .from("camere")
        .insert({ ...camera, user_id: authenticatedUser.id })
        .select()
        .single();
      
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      if (!data) throw new Error("Failed to create camera");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["camere"] });
    },
  });
}

export function useUpdateCamera() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Camera> }): Promise<Camera> => {
      const { data: result, error } = await supabase
        .from("camere")
        .update(data)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      if (!result) throw new Error("Failed to update camera");
      return result;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["camere"] });
      qc.invalidateQueries({ queryKey: ["camera", variables.id] });
    },
  });
}

// Stub hooks for other APIs (to be implemented with Supabase)
export function useGetCollaboratori(options?: { query?: { enabled?: boolean } }) {
  return useQuery({
    queryKey: ["collaboratori"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("collaboratori")
        .select("*")
        .eq("user_id", user.id)
        .order("nome");
      if (error) throw error;
      return data || [];
    },
    enabled: options?.query?.enabled !== false,
  });
}

export function useCreateCollaboratore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase.from("collaboratori").insert({ ...data, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collaboratori"] }),
  });
}

export function useUpdateCollaboratore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { error } = await supabase.from("collaboratori").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collaboratori"] }),
  });
}

export function useGetManutenzione(_params?: any, options?: { query?: { enabled?: boolean; queryKey?: any[] } }) {
  return useQuery({
    queryKey: options?.query?.queryKey || ["manutenzione"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("manutenzione")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: options?.query?.enabled !== false,
  });
}

export function useCreateTicketManutenzione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase.from("manutenzione").insert({ ...data, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manutenzione"] }),
  });
}

export function useUpdateTicketManutenzione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { error } = await supabase.from("manutenzione").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manutenzione"] }),
  });
}

export function useGetCamere(_params?: any) {
  return useQuery({
    queryKey: ["camere"],
    queryFn: async (): Promise<Camera[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("camere")
        .select("*")
        .eq("user_id", user.id)
        .order("nome");
      if (error) throw error;
      return data || [];
    },
  });
}

export interface Transazione {
  id: string;
  tipo: "Entrata" | "Uscita";
  descrizione?: string;
  categoria?: string;
  importo?: string | number;
  data?: string;
  struttura_id?: number;
  struttura_nome?: string;
  metodo_pagamento?: string;
  note?: string;
  user_id?: string;
  created_at?: string;
}

export function useGetTransazioni(options?: { query?: { enabled?: boolean } }) {
  return useQuery({
    queryKey: ["transazioni"],
    queryFn: async (): Promise<Transazione[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("transazioni")
        .select("*")
        .eq("user_id", user.id)
        .order("data", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: options?.query?.enabled !== false,
  });
}

export function useCreateTransazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Transazione>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase.from("transazioni").insert({ ...data, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transazioni"] }),
  });
}

export function useUpdateTransazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Transazione> }) => {
      const { error } = await supabase.from("transazioni").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transazioni"] }),
  });
}

export function useGetAttivita(options?: { query?: { enabled?: boolean } }) {
  return useQuery({
    queryKey: ["attivita"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("notifiche")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: options?.query?.enabled !== false,
  });
}

export function useCreateAttivita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase.from("attivita").insert({ ...data, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attivita"] }),
  });
}

export function useUpdateAttivita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { error } = await supabase.from("attivita").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attivita"] }),
  });
}

export function useGetHostProfile(id: string | number, options?: { query?: { enabled?: boolean; queryKey?: any[] } }) {
  return useQuery({
    queryKey: options?.query?.queryKey || ["host-profile", id],
    queryFn: async () => {
      if (!id) return null;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("host_profiles")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: options?.query?.enabled !== false && !!id,
  });
}

export function useGetHostProfiles() {
  return useQuery({
    queryKey: ["host-profiles"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("host_profiles")
        .select("*")
        .eq("user_id", user.id)
        .order("nome_display");
      if (error) throw error;
      return data || [];
    },
  });
}

const HOST_PROFILE_FIELDS = [
  "nome_display", "ruolo", "bio", "avatar", "verificato", "paese", "lingue", "interessi",
  "email", "regione", "telefono", "email_contatto", "whatsapp",
  "foto_copertina", "profile_boost", "punteggio", "num_recensioni", "tempo_risposta",
  "strutture_ids", "strutture_nomi",
] as const;
function pickHostProfileFields(data: any) {
  const out: Record<string, any> = {};
  for (const k of HOST_PROFILE_FIELDS) { if (k in data) out[k] = data[k]; }
  return out;
}

export function useCreateHostProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase
        .from("host_profiles")
        .insert({ ...pickHostProfileFields(data), user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["host-profiles"] }),
  });
}

export function useUpdateHostProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: any }) => {
      const { data: row, error } = await supabase
        .from("host_profiles")
        .update(pickHostProfileFields(data))
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return row;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["host-profiles"] });
      qc.invalidateQueries({ queryKey: ["host-profile", vars.id] });
    },
  });
}

/**
 * Sincronizza la relazione bidirezionale struttura ↔ host.
 * Chiamato dopo il salvataggio di una struttura o di un host profile.
 * - Aggiorna strutture.host_id con l'id dell'host selezionato
 * - Aggiorna host_profiles.strutture_ids aggiungendo/rimuovendo la struttura
 */
export async function syncHostStruttura({
  struttura_id,
  new_host_id,
  old_host_id,
}: {
  struttura_id: string;
  new_host_id: string | null;
  old_host_id: string | null;
}) {
  // 1. Aggiorna strutture.host_id
  await supabase.from("strutture").update({ host_id: new_host_id || null }).eq("id", struttura_id);

  // 2. Rimuovi struttura dal vecchio host (se cambiato)
  if (old_host_id && old_host_id !== new_host_id) {
    const { data: oldHost } = await supabase.from("host_profiles").select("strutture_ids").eq("id", old_host_id).single();
    if (oldHost) {
      let ids: string[] = [];
      try { ids = JSON.parse(oldHost.strutture_ids || "[]"); } catch { ids = []; }
      await supabase.from("host_profiles").update({ strutture_ids: JSON.stringify(ids.filter((x: string) => x !== struttura_id)) }).eq("id", old_host_id);
    }
  }

  // 3. Aggiungi struttura al nuovo host
  if (new_host_id) {
    const { data: newHost } = await supabase.from("host_profiles").select("strutture_ids, strutture_nomi").eq("id", new_host_id).single();
    if (newHost) {
      let ids: string[] = [];
      try { ids = JSON.parse(newHost.strutture_ids || "[]"); } catch { ids = []; }
      if (!ids.includes(struttura_id)) {
        const { data: strRow } = await supabase.from("strutture").select("nome").eq("id", struttura_id).single();
        let nomi: string[] = [];
        try { nomi = JSON.parse(newHost.strutture_nomi || "[]"); } catch { nomi = []; }
        ids.push(struttura_id);
        nomi.push(strRow?.nome ?? "");
        await supabase.from("host_profiles").update({ strutture_ids: JSON.stringify(ids), strutture_nomi: JSON.stringify(nomi) }).eq("id", new_host_id);
      }
    }
  }
}

export function useDeleteHostProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | number) => {
      const { error } = await supabase.from("host_profiles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["host-profiles"] }),
  });
}

// More stubs
export function useGetOspiti() {
  return useQuery({
    queryKey: ["ospiti"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("ospiti")
        .select("*")
        .eq("user_id", user.id)
        .order("cognome");
      if (error) throw error;
      return data || [];
    },
  });
}

const OSPITI_FIELDS = [
  "nome", "cognome", "email", "telefono", "whatsapp", "nazionalita",
  "sesso", "data_nascita", "luogo_nascita", "cittadinanza",
  "residenza", "indirizzo", "lingua",
  "ragione_sociale", "piva", "tipo",
  "tipo_documento", "num_documento", "luogo_rilascio",
  "consenso", "note",
] as const;

function pickOspiteFields(data: any) {
  const out: Record<string, any> = {};
  for (const k of OSPITI_FIELDS) {
    if (k in data) out[k] = data[k];
  }
  return out;
}

export function useCreateOspite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const payload = pickOspiteFields(data);
      const { data: row, error } = await supabase.from("ospiti").insert({ ...payload, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ospiti"] }),
  });
}

export function useUpdateOspite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const payload = pickOspiteFields(data);
      const { error } = await supabase.from("ospiti").update(payload).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ospiti"] }),
  });
}

export function useDeleteOspite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("ospiti").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ospiti"] }),
  });
}

export function useGetSegnalazioni() {
  return useQuery({
    queryKey: ["segnalazioni"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("segnalazioni")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateSegnalazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase.from("segnalazioni").insert({ ...data, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["segnalazioni"] }),
  });
}

export function useUpdateSegnalazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { error } = await supabase.from("segnalazioni").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["segnalazioni"] }),
  });
}

export function useGetAvvisi() {
  return useQuery({
    queryKey: ["avvisi"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("avvisi")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateAvviso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase
        .from("avvisi")
        .insert({ ...data, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["avvisi"] }),
  });
}

export function useUpdateAvviso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { error } = await supabase.from("avvisi").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["avvisi"] }),
  });
}

export function useGetRecensioni() {
  return useQuery({
    queryKey: ["recensioni"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("recensioni")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateRecensione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: row, error } = await supabase.from("recensioni").insert({ ...data, user_id: user.id }).select().single();
      if (error) throw error;
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recensioni"] }),
  });
}

export function useUpdateRecensione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const { error } = await supabase.from("recensioni").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recensioni"] }),
  });
}

export function useGetFinanzeReport() {
  return useQuery({
    queryKey: ["finanze-report"],
    queryFn: async () => [],
  });
}

export function useDeleteTransazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("transazioni").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transazioni"] }),
  });
}

export function useGetICalFeeds() {
  return useQuery({
    queryKey: ["ical_feeds"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("ical_sync")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateICalFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (feed: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("ical_sync")
        .insert({ ...feed, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ical_feeds"] }),
  });
}

export function useDeleteICalFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("ical_sync").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ical_feeds"] }),
  });
}

export function useGetContratti() {
  return useQuery({
    queryKey: ["contract_templates"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("contract_templates")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
}

const CONTRATTO_TEMPLATE_FIELDS = ["nome", "tipo", "contenuto", "attivo"] as const;
function pickContrattoTemplateFields(data: any) {
  const out: Record<string, any> = {};
  for (const k of CONTRATTO_TEMPLATE_FIELDS) { if (k in data) out[k] = data[k]; }
  return out;
}

export function useCreateContratto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("contract_templates")
        .insert({ ...pickContrattoTemplateFields(payload), user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contract_templates"] }),
  });
}

export function useUpdateContratto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { error } = await supabase.from("contract_templates").update(pickContrattoTemplateFields(data)).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contract_templates"] }),
  });
}

export function useDeleteContratto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("contract_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contract_templates"] }),
  });
}
